import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {Button, FormControl, Textarea, Banner, IconButton} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useCallback, useRef, useState, forwardRef, useImperativeHandle, useMemo, useId} from 'react'
import {GitBranchIcon} from '@primer/octicons-react'
import {GitBranchCheckIcon} from '@github-ui/pacer/CustomIcon'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'
import {
  COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER,
  COPILOT_INSTRUCTIONS_MAX_LENGTH,
  COPILOT_ASSIGNMENT_BRANCH_CAPTION,
  COPILOT_ASSIGNMENT_REPO_LABEL,
  COPILOT_ASSIGNMENT_BRANCH_LABEL,
  COPILOT_ASSIGNMENT_AGENT_LABEL,
  COPILOT_ASSIGNMENT_PROMPT_LABEL,
  COPILOT_ASSIGNMENT_SELECT_BRANCH_HEADING,
} from '../../utils/constants'
import {
  useCopilotRepositoryFetching,
  type DropdownRepository,
  type SimpleRepository,
} from '../../hooks/useCopilotRepoFetching'
import {useDefaultOwnerAwareRepositoryLoader} from '../../hooks/useDefaultOwnerAwareRepositoryLoader'
import styles from './CopilotAssignmentForm.module.css'
import {CommandButton} from '@github-ui/ui-commands'
import {AgentSelector} from '../new-task-chat-input/AgentSelector'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ModelPicker} from '../task-view/ModelPicker'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'

const CROSS_ORG_WARNING_TEXT =
  "You're assigning Copilot coding agent to open a pull request in a different organization. The issue description will be visible to users outside this repository."
const PRIVATE_TO_PUBLIC_WARNING_TEXT =
  "You're assigning Copilot coding agent to open a pull request in a public repository. The issue description will be visible to users outside this repository."
const EMPTY_REPO_WARNING_TEXT =
  "The selected repo has no commits or branches so there's no valid base branch to create a PR against."

export interface CopilotAssignmentFormRef {
  submit: () => Promise<void>
}

export interface CopilotAssignmentFormProps {
  initialRepo: SimpleRepository
  onSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedSubAgent: string | null | undefined,
    selectedModel: string | undefined,
  ) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>
  textareaAriaDescribedBy?: string
  nested?: boolean
}

export const CopilotAssignmentForm = forwardRef<CopilotAssignmentFormRef, CopilotAssignmentFormProps>(
  function CopilotAssignmentForm(
    {
      initialRepo,
      onSubmit,
      onCancel,
      isSubmitting = false,
      textareaRef,
      textareaAriaDescribedBy,
    }: CopilotAssignmentFormProps,
    ref,
  ) {
    const [content, setContent] = useState('')
    const [textareaValidationError, setTextareaValidationError] = useState<string | undefined>(undefined)
    const [branchValidationError, setBranchValidationError] = useState<string | undefined>(undefined)
    const [selectedSubAgent, setSelectedSubAgent] = useState<string | null>(null)
    const [selectedModel, setSelectedModel] = useState<string | undefined>(undefined)

    // Generate a unique cache key per modal mount to prevent stale branch data
    const cacheKey = `copilot-assignment-ref-selector-${useId()}`

    const internalTextareaRef = useRef<HTMLTextAreaElement>(null)
    const finalTextareaRef = textareaRef || internalTextareaRef

    // Create defaultOwner-aware repository loader that ensures initial repo is always fetched
    const initialRepoNameWithOwner = `${initialRepo.owner.login}/${initialRepo.name}`

    const defaultOwnerAwareRepositoryLoader = useDefaultOwnerAwareRepositoryLoader(
      initialRepo.owner.login,
      initialRepoNameWithOwner,
    )

    // Use the shared repository fetching hook
    const {
      selectedRepo,
      selectedBranch,
      setSelectedBranch,
      isLoadingBranch,
      initialRepoApiMetadata,
      handleRepoSelect: handleRepoSelectFromHook,
      handleReposLoaded,
    } = useCopilotRepositoryFetching(initialRepo)

    const isSweSubagentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')

    const warningMessage = useMemo(() => {
      if (!selectedRepo) return null

      const targetIsPublic = !selectedRepo.private
      const differentOrganization = initialRepo.owner.login !== selectedRepo.owner

      if (selectedRepo.isEmpty) {
        return EMPTY_REPO_WARNING_TEXT
      }

      if (differentOrganization) {
        return CROSS_ORG_WARNING_TEXT
      }

      if (initialRepoApiMetadata?.private && targetIsPublic) {
        return PRIVATE_TO_PUBLIC_WARNING_TEXT
      }

      return null
    }, [selectedRepo, initialRepo.owner.login, initialRepoApiMetadata])

    // Wrapper to prevent selection during submission
    const handleRepoSelect = useCallback(
      (repo: DropdownRepository | undefined) => {
        if (!repo || isSubmitting) return
        handleRepoSelectFromHook(repo)
        setBranchValidationError(undefined)
        setSelectedSubAgent(null)
      },
      [isSubmitting, handleRepoSelectFromHook],
    )

    const handleSubmit = useCallback(async () => {
      if (isSubmitting) return

      setTextareaValidationError(undefined)
      setBranchValidationError(undefined)

      // Validate character limit
      if (content.length > COPILOT_INSTRUCTIONS_MAX_LENGTH) {
        const errorMessage = `Instructions must be ${COPILOT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} characters or fewer. Current length: ${content.length.toLocaleString()} characters.`
        setTextareaValidationError(errorMessage)
        return
      }

      if (!selectedRepo || !selectedBranch) {
        setBranchValidationError('Please select a repository and branch.')
        return
      }

      if (selectedRepo.isEmpty) {
        setBranchValidationError('Please select a valid base branch.')
        return
      }

      await onSubmit(content, selectedRepo, selectedBranch, selectedSubAgent, selectedModel)
    }, [content, selectedRepo, selectedBranch, selectedSubAgent, selectedModel, onSubmit, isSubmitting])

    // Expose submit method through ref
    useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit,
      }),
      [handleSubmit],
    )

    const submitButtonInactive = selectedRepo?.isEmpty || isSubmitting || !selectedRepo || !selectedBranch
    const refSelectorInactive = isSubmitting || isLoadingBranch || !selectedRepo?.defaultBranch || selectedRepo?.isEmpty
    const customAgentPickerInactive = isSubmitting || selectedRepo?.isEmpty || !selectedBranch

    return (
      <div className={styles.formContainer}>
        <div className={styles.textareaContainer}>
          <FormControl disabled={isSubmitting}>
            <FormControl.Label>{COPILOT_ASSIGNMENT_PROMPT_LABEL}</FormControl.Label>
            <Textarea
              ref={finalTextareaRef}
              className={styles.textarea}
              placeholder={COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER}
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={2}
              resize="vertical"
              aria-describedby={textareaAriaDescribedBy}
              maxLength={COPILOT_INSTRUCTIONS_MAX_LENGTH}
            />
            {textareaValidationError && (
              <FormControl.Validation variant="error">{textareaValidationError}</FormControl.Validation>
            )}
          </FormControl>
        </div>

        <div className={styles.selectorsContainer}>
          <div className={styles.selectorsRow}>
            <FormControl className={styles.formControl}>
              <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_REPO_LABEL}</FormControl.Label>
              <ReposSelector
                repositoryLoader={defaultOwnerAwareRepositoryLoader}
                selectionVariant="single"
                selectAllOption={false}
                onSelect={handleRepoSelect}
                onLoad={handleReposLoaded}
                currentSelection={selectedRepo || undefined}
                buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
                width="large"
                truncateMaxWidth={250}
                additionalButtonProps={{
                  'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
                  disabled: isSubmitting,
                }}
              />
            </FormControl>

            <FormControl className={styles.formControl}>
              <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_BRANCH_LABEL}</FormControl.Label>
              {selectedRepo || selectedBranch ? (
                <RefSelector
                  useFocusZone
                  cacheKey={cacheKey}
                  canCreate={false}
                  currentCommitish={selectedBranch || selectedRepo?.defaultBranch || ''}
                  defaultBranch={selectedRepo?.defaultBranch ?? selectedBranch}
                  owner={selectedRepo?.owner || ''}
                  repo={selectedRepo?.nameOnly || ''}
                  types={['branch']}
                  selectedRefType="branch"
                  closeOnSelect
                  hideShowAll
                  disabled={refSelectorInactive}
                  actionListHeadingText={COPILOT_ASSIGNMENT_SELECT_BRANCH_HEADING}
                  actionListDescription={COPILOT_ASSIGNMENT_BRANCH_CAPTION}
                  onSelectItem={selectedRef => {
                    if (!isSubmitting) {
                      setSelectedBranch(selectedRef)
                    }
                  }}
                  renderAnchor={props => (
                    <IconButtonSelector
                      {...props}
                      icon={GitBranchIcon}
                      selectedIcon={GitBranchCheckIcon}
                      selected={selectedBranch !== selectedRepo?.defaultBranch}
                      aria-label={
                        selectedRepo?.isEmpty
                          ? 'Branch: None available'
                          : `Branch: ${isLoadingBranch ? 'Loading branch...' : selectedBranch || 'Select branch'}`
                      }
                      aria-labelledby={undefined}
                    />
                  )}
                  anchoredOverlayProps={{
                    variant: {regular: 'anchored', narrow: 'fullscreen'},
                    height: 'small',
                  }}
                />
              ) : (
                <IconButton icon={GitBranchIcon} aria-label="Loading branches…" disabled />
              )}
            </FormControl>
            {isSweSubagentsEnabled && selectedRepo && (
              <FormControl className={styles.formControl}>
                <FormControl.Label visuallyHidden>{COPILOT_ASSIGNMENT_AGENT_LABEL}</FormControl.Label>
                <AgentSelector
                  repo={selectedRepo}
                  branch={selectedRepo.defaultBranch}
                  selectedAgent={selectedSubAgent}
                  onSelect={setSelectedSubAgent}
                  iconOnly
                  disabled={customAgentPickerInactive}
                />
              </FormControl>
            )}
            {copilotFeatureFlags.codingAgentModelSelection && (
              <div className={styles.modelPickerContainer}>
                <FormControl>
                  <FormControl.Label visuallyHidden>Model</FormControl.Label>
                  <ModelPicker onUpdateModel={setSelectedModel} selectedModelName={selectedModel} />
                </FormControl>
              </div>
            )}
          </div>

          {warningMessage && (
            <Banner variant="warning" title="Warning" hideTitle className={styles.warningBanner}>
              {warningMessage}
            </Banner>
          )}
        </div>

        {branchValidationError && (
          <InlineMessage variant="critical" className={styles.branchValidation}>
            {branchValidationError}
          </InlineMessage>
        )}

        <div className={styles.buttonsContainer}>
          <div className={styles.buttonsRow}>
            {onCancel && (
              <Button variant="invisible" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <CommandButton
              commandId="github:submit-form"
              showKeybindingHint
              variant="primary"
              inactive={submitButtonInactive}
              loading={isSubmitting}
              className={styles.assignButton}
            >
              Assign
            </CommandButton>
          </div>
        </div>
      </div>
    )
  },
)

try{ CopilotAssignmentForm.displayName ||= 'CopilotAssignmentForm' } catch {}
try{ CopilotAssignmentForm.displayName ||= 'CopilotAssignmentForm' } catch {}